// Two-pane chat used by the staff inbox and the clinic portal. Left: threads with unread badges.
// Right: the conversation + composer (text and/or one attachment). Live via the chat.* SSE events.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CHAT_DEPARTMENTS,
  CHAT_DEPARTMENT_LABELS,
  formatDateTime,
  type Clinic,
  type ChatThreadListItem,
  type ChatMessage,
} from '@alignova/shared';
import { api } from '../lib/api.ts';
import { chatApi } from '../lib/chatApi.ts';
import { useLiveEvent } from '../lib/useLive.ts';

export function ChatPanel({ mode }: { mode: 'staff' | 'clinic' }) {
  const [threads, setThreads] = useState<ChatThreadListItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [subjectOfActive, setSubjectOfActive] = useState<string>('');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  const loadThreads = useCallback(() => {
    chatApi
      .threads()
      .then((r) => setThreads(r.threads))
      .catch(() => setError('Failed to load chats'));
  }, []);

  const loadDetail = useCallback((id: string) => {
    chatApi
      .thread(id)
      .then((r) => {
        setMessages(r.messages);
        setSubjectOfActive(r.thread.subject ?? r.thread.clinicName ?? 'Conversation');
      })
      .catch(() => setError('Failed to load conversation'));
  }, []);

  useEffect(loadThreads, [loadThreads]);
  useEffect(() => {
    if (activeId) loadDetail(activeId);
  }, [activeId, loadDetail]);

  // Realtime: any new message / thread refreshes the inbox, and the open conversation if affected.
  useLiveEvent<{ threadId?: string }>('chat.message', (p) => {
    loadThreads();
    if (activeId && (!p?.threadId || p.threadId === activeId)) loadDetail(activeId);
  });
  useLiveEvent('chat.thread', loadThreads);

  // Keep scrolled to the newest message.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  const openThread = (id: string) => {
    setActiveId(id);
    // Optimistically clear the unread badge; the GET marks it read server-side.
    setThreads((ts) => ts.map((t) => (t.id === id ? { ...t, unread: 0 } : t)));
  };

  const send = async () => {
    if (!activeId || (!text.trim() && !file)) return;
    setSending(true);
    setError(null);
    try {
      const sent = await chatApi.send(activeId, { body: text.trim(), file });
      setText('');
      setFile(null);
      // Optimistic append; the chat.message SSE event will reconcile everything else.
      setMessages((m) => (m.some((x) => x.id === sent.id) ? m : [...m, sent]));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const active = threads.find((t) => t.id === activeId) ?? null;

  // The panel owns its own scroll, so its height has to account for the chrome around it —
  // including the phone tab bar, or the composer ends up underneath it.
  return (
    <div className="flex h-[calc(100dvh-15rem)] min-h-[20rem] overflow-hidden rounded-xl border border-line bg-surface lg:h-[calc(100dvh-11rem)] lg:min-h-[24rem]">
      {/* Thread list */}
      <div className={`w-full flex-col border-r border-line md:flex md:w-80 ${activeId ? 'hidden md:flex' : 'flex'}`}>
        <div className="flex items-center justify-between border-b border-line px-3 py-2.5">
          <span className="text-sm font-semibold text-ink">Conversations</span>
          <button
            onClick={() => setShowNew(true)}
            className="rounded-lg bg-primary px-2.5 py-1 text-xs font-medium text-primary-fg hover:opacity-90"
          >
            ＋ New
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 && <p className="p-4 text-sm text-muted">No conversations yet.</p>}
          {threads.map((t) => (
            <button
              key={t.id}
              onClick={() => openThread(t.id)}
              className={`flex w-full flex-col gap-0.5 border-b border-line px-3 py-2.5 text-left hover:bg-surface-2 ${activeId === t.id ? 'bg-surface-2' : ''}`}
            >
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                  {t.subject || t.clinicName || 'Conversation'}
                </span>
                {t.unread > 0 && (
                  <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-fg">{t.unread}</span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted">
                {mode === 'staff' && t.clinicName && <span className="truncate">{t.clinicName}</span>}
                {t.department && <span className="rounded bg-surface px-1">{CHAT_DEPARTMENT_LABELS[t.department] ?? t.department}</span>}
              </div>
              {t.lastMessage && <div className="truncate text-xs text-muted">{t.lastMessage}</div>}
            </button>
          ))}
        </div>
      </div>

      {/* Conversation */}
      <div className={`flex-1 flex-col ${activeId ? 'flex' : 'hidden md:flex'}`}>
        {!activeId ? (
          <div className="grid flex-1 place-items-center text-sm text-muted">Select a conversation.</div>
        ) : (
          <>
            <div className="flex items-center gap-2 border-b border-line px-3 py-2.5">
              <button className="text-sm text-muted md:hidden" onClick={() => setActiveId(null)}>
                ‹ Back
              </button>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-ink">{subjectOfActive}</div>
                {mode === 'staff' && active?.clinicName && <div className="text-xs text-muted">{active.clinicName}</div>}
              </div>
            </div>

            <div className="flex-1 space-y-2 overflow-y-auto bg-surface-2 p-3">
              {messages.map((m) => (
                <MessageBubble key={m.id} m={m} mine={isMine(m, mode)} threadId={activeId} />
              ))}
              <div ref={endRef} />
            </div>

            <div className="border-t border-line p-2">
              {error && <p className="mb-1 px-1 text-xs text-status-urgent-fg">{error}</p>}
              {file && (
                <div className="mb-1 flex items-center gap-2 px-1 text-xs text-muted">
                  📎 {file.name}
                  <button className="text-status-urgent-fg" onClick={() => setFile(null)}>
                    remove
                  </button>
                </div>
              )}
              <div className="flex items-end gap-2">
                <label className="grid h-9 w-9 shrink-0 cursor-pointer place-items-center rounded-lg border border-line text-muted hover:bg-surface-2" title="Attach">
                  📎
                  <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                </label>
                <textarea
                  className="max-h-32 min-h-[2.25rem] flex-1 resize-none rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
                  rows={1}
                  placeholder="Type a message…"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                />
                <button
                  onClick={() => void send()}
                  disabled={sending || (!text.trim() && !file)}
                  className="h-9 shrink-0 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-fg hover:opacity-90 disabled:opacity-50"
                >
                  {sending ? '…' : 'Send'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showNew && (
        <NewThreadModal
          mode={mode}
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            loadThreads();
            setActiveId(id);
          }}
        />
      )}
    </div>
  );
}

/**
 * Patient chat: one conversation with the patient's own clinic. The server resolves the clinic from
 * the patient's case, so there is no picker and no "new conversation" form - the first message
 * starts the thread, later ones continue it.
 */
export function PatientChat({ patientName }: { patientName: string }) {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [clinicName, setClinicName] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(() => {
    chatApi
      .threads()
      .then(async (r) => {
        const t = r.threads[0]; // newest first
        if (!t) return;
        setThreadId(t.id);
        setClinicName(t.clinicName);
        setMessages((await chatApi.thread(t.id)).messages);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load chat'))
      .finally(() => setLoaded(true));
  }, []);
  useEffect(load, [load]);
  useLiveEvent('chat.message', load);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  const send = async () => {
    if (!text.trim() && !file) return;
    setSending(true);
    setError(null);
    try {
      let id = threadId;
      if (!id) {
        const t = await chatApi.create({ subject: `Patient - ${patientName}` });
        id = t.id;
        setThreadId(id);
        setClinicName(t.clinicName);
      }
      const sent = await chatApi.send(id, { body: text.trim(), file });
      setText('');
      setFile(null);
      setMessages((m) => (m.some((x) => x.id === sent.id) ? m : [...m, sent]));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-[calc(100dvh-15rem)] min-h-[20rem] flex-col overflow-hidden rounded-xl border border-line bg-surface lg:h-[calc(100dvh-11rem)]">
      <div className="border-b border-line px-3 py-2.5">
        <div className="truncate text-sm font-semibold text-ink">{clinicName ?? 'Your clinic'}</div>
        <div className="text-xs text-muted">Messages go straight to your clinic.</div>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto bg-surface-2 p-3">
        {loaded && messages.length === 0 && <p className="p-4 text-center text-sm text-muted">Send a message to your clinic.</p>}
        {threadId && messages.map((m) => <MessageBubble key={m.id} m={m} mine={m.senderType === 'patient'} threadId={threadId} />)}
        <div ref={endRef} />
      </div>
      <div className="border-t border-line p-2">
        {error && <p className="mb-1 px-1 text-xs text-status-urgent-fg">{error}</p>}
        {file && (
          <div className="mb-1 flex items-center gap-2 px-1 text-xs text-muted">
            📎 {file.name}
            <button className="text-status-urgent-fg" onClick={() => setFile(null)}>
              remove
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <label className="grid h-9 w-9 shrink-0 cursor-pointer place-items-center rounded-lg border border-line text-muted hover:bg-surface-2" title="Attach">
            📎
            <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label>
          <textarea
            className="max-h-32 min-h-[2.25rem] flex-1 resize-none rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
            rows={1}
            placeholder="Type a message…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <button
            onClick={() => void send()}
            disabled={sending || (!text.trim() && !file)}
            className="h-9 shrink-0 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-fg hover:opacity-90 disabled:opacity-50"
          >
            {sending ? '…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

function isMine(m: ChatMessage, mode: 'staff' | 'clinic'): boolean {
  return (mode === 'staff' && m.senderType === 'staff') || (mode === 'clinic' && m.senderType === 'clinic');
}

function MessageBubble({ m, mine, threadId }: { m: ChatMessage; mine: boolean; threadId: string }) {
  if (m.senderType === 'system') {
    return <div className="text-center text-xs text-muted">{m.body}</div>;
  }
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-primary text-primary-fg' : 'bg-surface text-ink'}`}>
        {!mine && m.senderName && <div className="mb-0.5 text-[11px] font-semibold opacity-70">{m.senderName}</div>}
        {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
        {m.hasAttachment && (
          <a
            href={chatApi.attachmentUrl(threadId, m.id)}
            target="_blank"
            rel="noreferrer"
            className={`mt-1 inline-block text-xs underline ${mine ? 'text-primary-fg' : 'text-primary'}`}
          >
            📎 {m.attachmentName ?? 'Attachment'}
          </a>
        )}
        <div className={`mt-0.5 text-[10px] ${mine ? 'text-primary-fg/70' : 'text-muted'}`}>{formatDateTime(m.createdAt)}</div>
      </div>
    </div>
  );
}

function NewThreadModal({
  mode,
  onClose,
  onCreated,
}: {
  mode: 'staff' | 'clinic';
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [subject, setSubject] = useState('');
  const [department, setDepartment] = useState<string>('general');
  const [clinicId, setClinicId] = useState('');
  const [firstMsg, setFirstMsg] = useState('');
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'staff') api.get<Clinic[]>('/clinics').then(setClinics).catch(() => setClinics([]));
  }, [mode]);

  const create = async () => {
    if (!subject.trim() || (mode === 'staff' && !clinicId)) {
      setErr('Pick a clinic and a subject.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const t = await chatApi.create({
        clinicId: mode === 'staff' ? clinicId : undefined,
        subject: subject.trim(),
        department,
        body: firstMsg.trim() || null,
      });
      onCreated(t.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary';
  return (
    <div className="fixed inset-0 z-modal grid place-items-center bg-navy/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-surface p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-3 text-base font-semibold text-ink">New conversation</h3>
        {err && <p className="mb-2 text-xs text-status-urgent-fg">{err}</p>}
        <div className="space-y-3">
          {mode === 'staff' && (
            <div>
              <label className="mb-1 block text-xs text-muted">Clinic</label>
              <select className={inputCls} value={clinicId} onChange={(e) => setClinicId(e.target.value)}>
                <option value="">Select clinic…</option>
                {clinics.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.clinicCode} - {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs text-muted">Subject</label>
            <input className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What is this about?" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted">Department</label>
            <select className={inputCls} value={department} onChange={(e) => setDepartment(e.target.value)}>
              {CHAT_DEPARTMENTS.map((d) => (
                <option key={d.key} value={d.key}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted">First message (optional)</label>
            <textarea className={inputCls} rows={2} value={firstMsg} onChange={(e) => setFirstMsg(e.target.value)} />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-2">
            Cancel
          </button>
          <button onClick={() => void create()} disabled={busy} className="rounded-lg bg-primary px-4 py-1.5 text-sm font-semibold text-primary-fg hover:opacity-90 disabled:opacity-50">
            {busy ? 'Creating…' : 'Start'}
          </button>
        </div>
      </div>
    </div>
  );
}
